import { lowerFirst } from 'lodash';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IAuthUser } from 'auth/types';
import { useCliJobHandler } from 'core/hooks';
import { useAgGridTableContext } from 'core/states';
import { ISubmitAction } from 'core/types';
import { useQuoteVersioningContext } from 'quotes/components/CustomerQuotePartList';
import { QuoteEntityType, ReleaseType } from 'quotes/enums';
import { useQuoteVersionActions } from 'quotes/hooks';
import { mapReleaseCustomerQuoteForm } from 'quotes/mappers';
import { ICustomerQuote, IQuoteReleaseVersionForm } from 'quotes/types';

/**
 * Submit actions for QuoteVersionReleaseForm component.
 */
export const useSubmitActions = (
  quote: ICustomerQuote,
  onClose: () => void,
  user: IAuthUser,
  entityType = QuoteEntityType.QUOTE
) => {
  const { t } = useTranslation();

  const { gridApi } = useAgGridTableContext();
  const {
    onReleaseDraftSuccess,
    onReleaseDraftError,
    onReleaseDraftPollFailed,
    setReleaseButtonDisabled,
  } = useQuoteVersioningContext();

  const { handleCliJob } = useCliJobHandler();
  const { releaseDraftVersion } = useQuoteVersionActions();

  const getSubmitActions = (): ISubmitAction[] => {
    const submitActions = [
      {
        type: ReleaseType.EMAIL,
        label: t('buttonLabelSendRelease'),
        onSubmit: (reset) => (data, submitType) => onQuoteRelease(data, submitType, reset),
      },
    ];

    if (user?.settings?.releaseQuoteNoEmailEnabled) {
      submitActions.push({
        type: ReleaseType.NO_EMAIL,
        label: t('buttonLabelReleaseNoEmail'),
        onSubmit: (reset) => (data, submitType) => onQuoteRelease(data, submitType, reset),
      });
    }

    return submitActions;
  };

  const onQuoteRelease = (
    data,
    submitType: ReleaseType,
    reset: UseFormReset<IQuoteReleaseVersionForm>
  ) => {
    gridApi?.setGridOption('loading', true);
    handleOnClose(reset, true);

    handleCliJob(releaseDraftVersion, mapReleaseCustomerQuoteForm(quote.id, submitType, data), {
      cliJobInProgressMessage: t('messageReleaseQuoteInProgress', {
        entityType: lowerFirst(entityType),
      }),
      onCliJobFinished: onReleaseDraftSuccess,
      onCliJobStopped: onReleaseDraftError,
      onCliJobPollFailed: onReleaseDraftPollFailed,
    });
  };

  const handleOnClose = (reset: UseFormReset<IQuoteReleaseVersionForm>, withSuccess = false) => {
    reset({
      attachPdf: false,
      attachXls: false,
      note: '',
    });

    if (!withSuccess) {
      setReleaseButtonDisabled(false);
    }

    onClose();
  };

  return {
    getSubmitActions,
    handleOnClose,
  };
};
